import DragDropManager from './DragDropManager';
import { Utils } from '../utils/utils';
import DragTransform from '../interfaces/DragTransform';
import { DragDropBase } from "./DragDropBase";
import HTMLElementExtendOptions from "../interfaces/HTMLElementExtendOptions";
import DragDropUIData from '../interfaces/DragDropUIData';
import ItemHTMLElement from '../interfaces/ItemHTMLElement';
import DragDropDraggableOptions from '../interfaces/DragDropDraggableOptions';
import DragOffset from '../interfaces/DragOffset';
import NodeRotate from '../interfaces/NodeRotate';
import DragDropElementHost from '../interfaces/DragDropElementHost';
import { isTouch, touchend, touchmove, touchstart, pointerdown } from '../utils/touchUtils';
import { DragDropDragEvent } from '../types/DragDropDragEvent';

// make sure we are not clicking on known object that handles mouseDown
const skipMouseDown = 'input,textarea,button,select,option,[contenteditable="true"],.ui-resizable-handle';

/**
 * The draggable class
 */
export default class DragDropDraggable extends DragDropBase implements HTMLElementExtendOptions<DragDropDraggableOptions>
{
  public helper: HTMLElement;

  /** @internal */
  protected mouseDownEvent: MouseEvent;
  /** @internal */
  protected dragOffset: DragOffset;
  /** @internal */
  protected dragElementOriginStyle: Array<string>;
  /** @internal */
  protected dragEls: HTMLElement[];
  /** @internal true while we are dragging an item around */
  protected dragging: boolean;
  /** @internal last drag event */
  protected lastDrag: DragEvent;
  /** @internal */
  protected parentOriginStylePosition: string;
  /** @internal */
  protected helperContainment: HTMLElement;
  /** @internal properties we change during dragging, and restore back */
  protected static originStyleProp = [ 'transition', 'pointerEvents', 'position', 'left', 'top', 'minWidth', 'willChange' ];
  /** @internal pause before we call the actual drag hit collision code */
  protected dragTimeout: number;
  /** @internal */
  protected dragTransform: DragTransform = {
    xScale: 1,
    yScale: 1,
    xOffset: 0,
    yOffset: 0
  };

  /**
   * Constructor
   * @param el The element to make draggable
   * @param option The options for the draggable
   */
  constructor (public el: ItemHTMLElement, public option: DragDropDraggableOptions = {})
  {
    super();

    // get the element that is actually supposed to be dragged by
    const handleName = option.handle.substring(1);
    const n = el.tileLayoutNode;
    this.dragEls = el.classList.contains(handleName) ? [ el ] : (n?.subLayout 
      ? [ el.querySelector(option.handle) || el ] : Array.from(el.querySelectorAll(option.handle)));

    if (this.dragEls.length === 0)
    {
      this.dragEls = [ el ];
    }

    // create var event binding so we can easily remove and still look like TS methods (unlike anonymous functions)
    this._mouseDown = this._mouseDown.bind(this);
    this._mouseMove = this._mouseMove.bind(this);
    this._mouseUp = this._mouseUp.bind(this);
    this._keyEvent = this._keyEvent.bind(this);
    this.enable();
  }

  /**
   * Add an event listener
   * @param event The event to listen for
   * @param callback The callback to call when the event is triggered
   */
  public on (event: DragDropDragEvent, callback: (event: DragEvent) => void): void
  {
    super.on(event, callback);
  }

  /**
   * Remove an event listener
   * @param event The event to remove the listener from
   */
  public off (event: DragDropDragEvent): void
  {
    super.off(event);
  }

  /**
   * Enable the draggable
   */
  public enable (): void
  {
    if (this.disabled === false) 
      return;

    super.enable();
    this.dragEls.forEach(dragEl =>
    {
      dragEl.addEventListener('mousedown', this._mouseDown);
      if (isTouch)
      {
        dragEl.addEventListener('touchstart', touchstart);
        dragEl.addEventListener('pointerdown', pointerdown);
      }
    });
    this.el.classList.remove('ui-draggable-disabled');
  }

  /**
   * Disable the draggable
   * @param forDestroy True if we're disabling for destroy
   */
  public disable (forDestroy = false): void
  {
    if (this.disabled === true) 
      return;

    super.disable();
    this.dragEls.forEach(dragEl =>
    {
      dragEl.removeEventListener('mousedown', this._mouseDown);
      if (isTouch)
      {
        dragEl.removeEventListener('touchstart', touchstart);
        dragEl.removeEventListener('pointerdown', pointerdown);
      }
    });

    if (!forDestroy) 
      this.el.classList.add('ui-draggable-disabled');
  }

  /**
   * Destroy the draggable
   */
  public destroy (): void
  {
    if (this.dragTimeout) 
      window.clearTimeout(this.dragTimeout);

    delete this.dragTimeout;
    if (this.mouseDownEvent) 
      this._mouseUp(this.mouseDownEvent);

    this.disable(true);
    delete this.el;
    delete this.helper;
    delete this.option;
    super.destroy();
  }

  /**
   * Update the options for the draggable
   * @param opts The new options
   * @returns {DragDropDraggable} The draggable instance
   */
  public updateOption (opts: DragDropDraggableOptions): DragDropDraggable
  {
    Object.keys(opts).forEach(key => this.option[ key ] = opts[ key ]);
    return this;
  }

  /** 
   * @internal 
   * Call when mouse goes down before a dragstart happens
   * @param {MouseEvent} e The mouse event
   * @returns {boolean} True if the event was handled
   */
  protected _mouseDown (e: MouseEvent): boolean
  {
    // don't let more than one item handle mouseStart
    if (DragDropManager.mouseHandled) 
      return;

    if (e.button !== 0)
      return true; // only left click

    // make sure we are not clicking on known object that handles mouseDown, or ones supplied by the user
    if (!this.dragEls.find(el => el === e.target) && (e.target as HTMLElement).closest(skipMouseDown)) 
      return true;

    if (this.option.cancel)
    {
      if ((e.target as HTMLElement).closest(this.option.cancel)) return true;
    }

    this.mouseDownEvent = e;
    delete this.dragging;
    delete DragDropManager.dragElement;
    delete DragDropManager.dropElement;
    // document handler so we can continue receiving moves as the item is 'fixed' position, and capture=true so WE get a first crack
    document.addEventListener('mousemove', this._mouseMove, { capture: true, passive: true }); // true=capture, not bubble
    document.addEventListener('mouseup', this._mouseUp, true);
    if (isTouch)
    {
      e.target.addEventListener('touchmove', touchmove);
      e.target.addEventListener('touchend', touchend);
    }

    // preventDefault() prevents blur event which occurs just after mousedown event.
    e.preventDefault();
    // if an editable content has focus, then blur must be call
    if (document.activeElement) 
      (document.activeElement as HTMLElement).blur();

    DragDropManager.mouseHandled = true;
    return true;
  }

  /** 
   * @internal 
   * Method to call actual drag event
   * @param {DragEvent} e The drag event
   */
  protected _callDrag (e: DragEvent): void
  {
    if (!this.dragging) 
      return;

    const ev = Utils.initEvent<DragEvent>(e, { target: this.el, type: 'drag' });
    if (this.option.drag)
    {
      this.option.drag(ev, this.ui());
    }
    this.triggerEvent('drag', ev);
  }

  /** 
   * @internal 
   * Called when the main page (after successful mousedown) receives a move event to drag the item around the screen 
   * @param {DragEvent} e The drag event
   * @returns {boolean} True if the event was handled
   */
  protected _mouseMove (e: DragEvent): boolean
  {
    let s = this.mouseDownEvent;
    this.lastDrag = e;

    if (this.dragging)
    {
      this._dragFollow(e);
      // delay actual tile layout handling drag until we pause for a while if set
      if (DragDropManager.pauseDrag)
      {
        const pause = Number.isInteger(DragDropManager.pauseDrag) ? DragDropManager.pauseDrag as number : 100;
        if (this.dragTimeout) window.clearTimeout(this.dragTimeout);
        this.dragTimeout = window.setTimeout(() => this._callDrag(e), pause);
      } else
      {
        this._callDrag(e);
      }
    } else if (Math.abs(e.x - s.x) + Math.abs(e.y - s.y) > 3)
    {
      /**
       * don't start unless we've moved at least 3 pixels
       */
      this.dragging = true;
      DragDropManager.dragElement = this;
      // if we're dragging an actual tile layout item, set the current drop as the tile layout (to detect enter/leave)
      let tileLayout = this.el.tileLayoutNode?.tileLayout;
      if (tileLayout)
      {
        DragDropManager.dropElement = (tileLayout.el as DragDropElementHost).ddElement.ddDroppable;
      } else
      {
        delete DragDropManager.dropElement;
      }
      this.helper = this._createHelper(e);
      this._setupHelperContainmentStyle();
      this.dragTransform = Utils.getValuesFromTransformedElement(this.helperContainment);
      this.dragOffset = this._getDragOffset(e, this.el, this.helperContainment);
      this._setupHelperStyle(e);

      const ev = Utils.initEvent<DragEvent>(e, { target: this.el, type: 'dragstart' });
      if (this.option.start)
      {
        this.option.start(ev, this.ui());
      }
      this.triggerEvent('dragstart', ev);
      // now track keyboard events to cancel or rotate
      document.addEventListener('keydown', this._keyEvent);
    }
    return true;
  }

  /** 
   * @internal 
   * Call when the mouse gets released to drop the item at current location 
   * @param {MouseEvent} e The mouse event
   */
  protected _mouseUp (e: MouseEvent): void
  {
    document.removeEventListener('mousemove', this._mouseMove, true);
    document.removeEventListener('mouseup', this._mouseUp, true);
    if (isTouch)
    {
      e.target.removeEventListener('touchmove', touchmove, true);
      e.target.removeEventListener('touchend', touchend, true);
    }

    if (this.dragging)
    {
      delete this.dragging;
      delete (this.el.tileLayoutNode as NodeRotate)?._originalPositionBeforeRotating;
      document.removeEventListener('keydown', this._keyEvent);

      // reset the drop target if dragging over ourself (already parented, just moving during stop callback below)
      if (DragDropManager.dropElement?.el === this.el.parentElement)
      {
        delete DragDropManager.dropElement;
      }

      this.helperContainment.style.position = this.parentOriginStylePosition || null;
      if (this.helper === this.el)
      {
        this._removeHelperStyle();
      } else
      {
        this.helper.remove();
      }
      const ev = Utils.initEvent<DragEvent>(e, { target: this.el, type: 'dragstop' });
      if (this.option.stop)
      {
        this.option.stop(ev); // NOTE: destroy() will be called when removing item, so expect NULL ptr after!
      }
      this.triggerEvent('dragstop', ev);

      // call the droppable method to receive the item
      if (DragDropManager.dropElement)
      {
        DragDropManager.dropElement.drop(e);
      }
    }
    delete this.helper;
    delete this.mouseDownEvent;
    delete DragDropManager.dragElement;
    delete DragDropManager.dropElement;
    delete DragDropManager.mouseHandled;
    e.preventDefault();
  }

  /** 
   * @internal 
   * Call when keys are being pressed - use Esc to cancel, R to rotate 
   * @param {KeyboardEvent} e The keyboard event
   */
  protected _keyEvent (e: KeyboardEvent): void
  {
    const n = this.el.tileLayoutNode as NodeRotate;
    if (!n?.tileLayout) 
      return;

    const tileLayout = n.tileLayout;

    if (e.key === 'Escape')
    {
      if (n._originalPositionBeforeRotating)
      {
        n._originalPosition = n._originalPositionBeforeRotating;
        delete n._originalPositionBeforeRotating;
      }
      tileLayout.engine.restoreInitial();
      this._mouseUp(this.mouseDownEvent);
    } else if (e.key === 'r' || e.key === 'R')
    {
      if (!Utils.canBeRotated(n))
        return;

      // store the real original size in case we Esc after doing rotation
      n._originalPositionBeforeRotating = n._originalPositionBeforeRotating || { ...n._originalPosition }; 
      delete n._moving; // force rotate to happen (move waits for >50% coverage otherwise)
      tileLayout.setAnimation(false) // immediate rotate so _getDragOffset() gets the right dom size below
        .rotate(n.el, { top: -this.dragOffset.offsetTop, left: -this.dragOffset.offsetLeft })
        .setAnimation();
      n._moving = true;
      this.dragOffset = this._getDragOffset(this.lastDrag, n.el, this.helperContainment);
      this.helper.style.width = this.dragOffset.width + 'px';
      this.helper.style.height = this.dragOffset.height + 'px';
      Utils.swap(n._originalPosition, 'w', 'h');
      delete n._rect;
      this._mouseMove(this.lastDrag);
    }
  }

  /** 
   * @internal 
   * Create a clone copy (or user defined method) of the original drag item if set 
   * @param {DragEvent} event The drag event
   * @returns {HTMLElement} The helper element
   */
  protected _createHelper (event: DragEvent): HTMLElement
  {
    let helper = this.el;
    if (typeof this.option.helper === 'function')
    {
      helper = this.option.helper(event);
    } else if (this.option.helper === 'clone')
    {
      helper = Utils.cloneNode(this.el);
    }

    if (!document.body.contains(helper))
    {
      Utils.appendTo(helper, this.option.appendTo === 'parent' ? this.el.parentElement : this.option.appendTo);
    }

    if (helper === this.el)
    {
      this.dragElementOriginStyle = DragDropDraggable.originStyleProp.map(prop => this.el.style[ prop ]);
    }
    return helper;
  }

  /** 
   * @internal 
   * Set the fix position of the dragged item 
   * @param {DragEvent} e The drag event
   * @returns {DragDropDraggable}
   */
  protected _setupHelperStyle (e: DragEvent): DragDropDraggable
  {
    this.helper.classList.add('ui-draggable-dragging');
    // TODO: set all at once with style.cssText += ... ? https://stackoverflow.com/questions/3968593
    const style = this.helper.style;
    style.pointerEvents = 'none'; // needed for over items to get enter/leave
    // style.cursor = 'move'; //  TODO: can't set with pointerEvents=none ! (done in CSS as well)
    style.width = this.dragOffset.width + 'px';
    style.height = this.dragOffset.height + 'px';
    style.willChange = 'left, top';
    style.position = 'fixed'; // let us drag between layouts by not clipping as parent .fe-tile-layout is position: 'relative'
    this._dragFollow(e); // now position it
    style.transition = 'none'; // show up instantly
    setTimeout(() =>
    {
      if (this.helper)
      {
        style.transition = null; // recover animation
      }
    }, 0);
    return this;
  }

  /** 
   * @internal 
   * Restore back the original style before dragging 
   * @returns {DragDropDraggable}
   */
  protected _removeHelperStyle (): DragDropDraggable
  {
    this.helper.classList.remove('ui-draggable-dragging');
    let node = (this.helper as ItemHTMLElement)?.tileLayoutNode;
    // don't bother restoring styles if we're gonna remove anyway...
    if (!node?._isAboutToRemove && this.dragElementOriginStyle)
    {
      let helper = this.helper;
      // don't animate, otherwise we animate offseted when switching back to 'absolute' from 'fixed'.
      // TODO: this also removes resizing animation which doesn't have this issue, but others.
      // Ideally both would animate ('move' would immediately restore 'absolute' and adjust coordinate to match,
      // then trigger a delay (repaint) to restore to final dest with animate) but then we need to make sure 'resizestop'
      // is called AFTER 'transitionend' event is received
      let transition = this.dragElementOriginStyle[ 'transition' ] || null;
      helper.style.transition = this.dragElementOriginStyle[ 'transition' ] = 'none'; // can't be NULL #1973
      DragDropDraggable.originStyleProp.forEach(prop => helper.style[ prop ] = this.dragElementOriginStyle[ prop ] || null);
      setTimeout(() => helper.style.transition = transition, 50); // recover animation from saved vars after a pause (0 isn't enough #1973)
    }
    delete this.dragElementOriginStyle;
    return this;
  }

  /** 
   * @internal 
   * Updates the top/left position to follow the mouse 
   * @param {DragEvent} e The drag event
   */
  protected _dragFollow (e: DragEvent): void
  {
    let containmentRect = { left: 0, top: 0 };
    const style = this.helper.style;
    const offset = this.dragOffset;
    style.left = (e.clientX + offset.offsetLeft - containmentRect.left) * this.dragTransform.xScale + 'px';
    style.top = (e.clientY + offset.offsetTop - containmentRect.top) * this.dragTransform.yScale + 'px';
  }

  /** 
   * @internal 
   * Set the containment style of the helper element
   * @returns {DragDropDraggable}
   */
  protected _setupHelperContainmentStyle (): DragDropDraggable
  {
    this.helperContainment = this.helper.parentElement;
    if (this.helper.style.position !== 'fixed')
    {
      this.parentOriginStylePosition = this.helperContainment.style.position;
      if (getComputedStyle(this.helperContainment).position.match(/static/))
      {
        this.helperContainment.style.position = 'relative';
      }
    }
    return this;
  }

  /** 
   * @internal 
   * Get the offset of the dragged item
   * @param {DragEvent} event The drag event
   * @param {HTMLElement} el The element being dragged
   * @param {HTMLElement} parent The parent element
   * @returns {DragOffset} The offset of the dragged item
   */
  protected _getDragOffset (event: DragEvent, el: HTMLElement, parent: HTMLElement): DragOffset
  {
    // in case ancestor has transform/perspective css properties that change the viewpoint
    let xformOffsetX = 0;
    let xformOffsetY = 0;
    if (parent)
    {
      xformOffsetX = this.dragTransform.xOffset;
      xformOffsetY = this.dragTransform.yOffset;
    }

    const targetOffset = el.getBoundingClientRect();
    return {
      left: targetOffset.left,
      top: targetOffset.top,
      offsetLeft: - event.clientX + targetOffset.left - xformOffsetX,
      offsetTop: - event.clientY + targetOffset.top - xformOffsetY,
      width: targetOffset.width * this.dragTransform.xScale,
      height: targetOffset.height * this.dragTransform.yScale
    };
  }

  /** 
   * Get the UI data for the drag event
   * @returns {DragDropUIData} The UI data
   */
  public ui (): DragDropUIData
  {
    const containmentEl = this.el.parentElement;
    const containmentRect = containmentEl.getBoundingClientRect();
    const offset = this.helper.getBoundingClientRect();
    return {
      position: { 
        //Current CSS position of the helper as { top, left } object
        top: (offset.top - containmentRect.top) * this.dragTransform.yScale,
        left: (offset.left - containmentRect.left) * this.dragTransform.xScale
      }
    };
  }
}
